import { SHIPPING_CONNECTION_CODE } from "../../../models/IntegrationsTypes";
import {
    BOX_FREE_TYPE,
    CUSTOM_PACKAGE_SUPPORT_TYPE,
    DANGEROUS_GOODS_TYPE,
    DANGEROUS_GOODS_TYPE_TRANSLATION_KEYS_MAP,
    DEFAULT_HS_CODE_REQUIRED_TYPE,
    IAggregatorInfo,
    LABEL_TYPE,
    RETURN_LABELS_TYPE,
    TRACKING_TYPE
} from '../return-method-types';
import { SHIPMENT_STATUS_ENUM, SHIPMENT_STATUS_TRANSLATION_KEYS_MAP } from '../../manage-returns/rma-page/rma-types';
import { IOption } from '../../../components/inputs/select-dropdown/select-dropdown';

class AggregatorsInfoStore {

    private _aggregatorsInfo: IAggregatorInfo[];

    constructor(aggregatorsInfo: IAggregatorInfo[]) {
        this._aggregatorsInfo = aggregatorsInfo;
    }

    private _getAggregatorInfo(aggregatorCode: SHIPPING_CONNECTION_CODE | undefined): IAggregatorInfo | undefined {
        if (!aggregatorCode) {
            return undefined
        }

        return this._aggregatorsInfo.find(aggregator => aggregator.value === aggregatorCode);
    }

    private _getCapabilities(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getAggregatorInfo(aggregatorCode)?.capabilities;
    }

    private _getLabelSettings(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getAggregatorInfo(aggregatorCode)?.labelSettings;
    }

    /** Public methods */
    public isShipByReturnGo(aggregatorCode: SHIPPING_CONNECTION_CODE): boolean {
        return this._getAggregatorInfo(aggregatorCode)?.isShipByReturnGo ?? false;
    }

    public isCoverage(aggregatorCode: SHIPPING_CONNECTION_CODE): boolean {
        return this._getAggregatorInfo(aggregatorCode)?.isCoverage ?? false;
    }

    public isDropOffAggregator(aggregatorCode: SHIPPING_CONNECTION_CODE): boolean {
        return this._getAggregatorInfo(aggregatorCode)?.isDropOffIntegration ?? false;
    }

    public getIsSupportCarrierSelectionOptions(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.SUPPORT_CARRIER_SELECTION_OPTIONS ?? false;
    }

    public getUseCustomPackage(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): CUSTOM_PACKAGE_SUPPORT_TYPE|undefined {
        return this._getLabelSettings(aggregatorCode)?.USE_CUSTOM_PACKAGE;
    }

    public getTrackingType(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): TRACKING_TYPE|undefined {
        return this._getCapabilities(aggregatorCode)?.trackingType;
    }

    public getIsLabelCostSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.LABEL_COST ?? false;
    }

    public getTrackingNoteContent(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getAggregatorInfo(aggregatorCode)?.trackingNotSupportedNote;
    }

    public getTrackingHelpLink(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getAggregatorInfo(aggregatorCode)?.trackingHelpLink;
    }

    public getAggregtorLabel(  aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getAggregatorInfo(aggregatorCode)?.label;
    }

    public areInstructionsEditable(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getAggregatorInfo(aggregatorCode)?.areInstructionsEditable ?? true;
    }

    public isAddressConfigurable(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getLabelSettings(aggregatorCode)?.ADDRESS?.configurable ?? true;
    }

    public getAddressReasonNote(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getLabelSettings(aggregatorCode)?.ADDRESS?.reasonNote;
    }

    public isSupportedForwardLabel(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getCapabilities(aggregatorCode)?.FORWARD_LABELS ?? false;
    }

    public isSupportedReturnLabel(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getCapabilities(aggregatorCode)?.RETURN_LABELS !== RETURN_LABELS_TYPE.RETURN_LABELS_NEVER;
    }

    public getShouldSendOrderNumber(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getLabelSettings(aggregatorCode)?.SHOULD_SEND_ORDER_NUMBER ?? false;
    }

    public getDefaultLabelType(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getCapabilities(aggregatorCode)?.DEFAULT_LABEL_TYPE ?? LABEL_TYPE.LABEL_TYPE_PRINTABLE;
    }

    public getLabelTypeSupport(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getCapabilities(aggregatorCode)?.LABEL_TYPE_SUPPORT;
    }

    public getLabelTypeSupportNote(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getCapabilities(aggregatorCode)?.LABEL_TYPE_SUPPORT_NOTE ??
        "Select whether to send customers a printable label by email, a QR code to present at a drop-off location, or both label types.";
    }

    public getRequireSignature(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getLabelSettings(aggregatorCode)?.REQUIRE_SIGNATURE ?? false;
    }

    public isIgnoreAddressValidationSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getLabelSettings(aggregatorCode)?.IGNORE_ADDRESS_VALIDATION ?? false;
    }

    public getDefaultSignatureNote(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getLabelSettings(aggregatorCode)?.DEFAULT_SIGNATURE_NOTE ?? '';
    }

    public isSingleCarrier(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined) {
        return this._getLabelSettings(aggregatorCode)?.IS_SINGLE_CARRIER ?? false;
    }

    public isDefaultWeightRequired(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getLabelSettings(aggregatorCode)?.DEFAULT_WEIGHT_REQUIRED ?? true;
    }

    public getWarningTexts(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): string | undefined {
        return this._getAggregatorInfo(aggregatorCode)?.warningText;
    }

    public isMultipleShipmentsPerRmaSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.SUPPORT_MULTIPLE_SHIPMENTS_PER_RMA ?? true;
    }

    public isDropOffLocationsSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.DROP_OFF_LOCATION ?? false;
    }

    public isLocatorUrlConfigurable(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.IS_LOCATOR_URL_CONFIGURABLE ?? false;
    }

    public getLogoIconUrl(aggregatorCode: SHIPPING_CONNECTION_CODE | undefined): string | undefined {
        return this._getAggregatorInfo(aggregatorCode)?.logoIconUrl;
    }

    public isBoxFreeSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.BOX_FREE !== BOX_FREE_TYPE.NEVER;
    }

    public isBoxFreeConfigurable(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.BOX_FREE === BOX_FREE_TYPE.OPTIONAL;
    }

    public getBoxFreeInitialValue(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getCapabilities(aggregatorCode)?.BOX_FREE === BOX_FREE_TYPE.ALWAYS;
    }

    public isDefaultHsCodeSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return !!this._getLabelSettings(aggregatorCode)?.DEFAULT_HS_CODE_REQUIRED
          && this._getLabelSettings(aggregatorCode)?.DEFAULT_HS_CODE_REQUIRED !== DEFAULT_HS_CODE_REQUIRED_TYPE.DEFAULT_HS_CODE_REQUIRED_NEVER;
    }

    public isDefaultHsCodeRequired(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getLabelSettings(aggregatorCode)?.DEFAULT_HS_CODE_REQUIRED === DEFAULT_HS_CODE_REQUIRED_TYPE.DEFAULT_HS_CODE_REQUIRED_ALWAYS;
    }

    public isDangerousGoodsSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getLabelSettings(aggregatorCode)?.DANGEROUS_GOODS ?? false;
    }

    public isEmailAdressRequiredForCarrier(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return this._getLabelSettings(aggregatorCode)?.REQUIRE_EMAIL_ADDRESS_FOR_CARRIER ?? false;
    }

    public isArrivedAtHubToInternalStatusSupported(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return !!this._getLabelSettings(aggregatorCode)?.ARRIVED_AT_HUB_TO_INTERNAL_STATUS;
    }

    public getIsArrivedAtHubToInternalStatusOptionsList(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined, getTranslation: (key: string, scope: string) => string): IOption[] {
        const arrivedAtHubToInternalStatusOptionsList = this._getLabelSettings(aggregatorCode)?.ARRIVED_AT_HUB_TO_INTERNAL_STATUS?.values ?? [];

        return arrivedAtHubToInternalStatusOptionsList.map((value) => {
            return {
                value,
                label: getTranslation(SHIPMENT_STATUS_TRANSLATION_KEYS_MAP[value], 'dashboard-returns-rma-card'),
            }
        });
    }

    public getIsArrivedAtHubToInternalStatusInitialValue(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): SHIPMENT_STATUS_ENUM {
        return this._getLabelSettings(aggregatorCode)?.ARRIVED_AT_HUB_TO_INTERNAL_STATUS?.default ?? SHIPMENT_STATUS_ENUM.RECEIVED;
    }

    public getLabelSettingsNote(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): string|null|undefined {
        return this._getLabelSettings(aggregatorCode)?.LABEL_SETTINGS_NOTE;
    }

    public isCreateLabelOnExchangeConfigurable(aggregatorCode: SHIPPING_CONNECTION_CODE|undefined): boolean {
        return !!this._getLabelSettings(aggregatorCode)?.IS_CREATE_LABEL_ON_EXCHANGE_CONFIGURABLE;
    }

    public getDangerousGoodsTypeOptionsList(getTranslation: (key: string, scope: string) => string): IOption[] {
        return Object.values(DANGEROUS_GOODS_TYPE).map((value) => {
            return {
                value,
                label: getTranslation(DANGEROUS_GOODS_TYPE_TRANSLATION_KEYS_MAP[value], 'dashboard-return-method-editor'),
            }
        });
    }
}

export default AggregatorsInfoStore;
